import { BorderStyle, Box, Colors } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

import type CalendarDay from 'modules/attendance/store/models/CalendarDay'

export const isFutureDate = (calendarDay: CalendarDay) => {
  const date = stringToDate(calendarDay.attributes.date, 'backend')
  const currentDate = new Date()

  return currentDate <= date.toDate()
}

export const setBackground = (
  calendarDay: CalendarDay,
  isTab = false
): 'champagne100' | 'grey100' | 'grey000White' => {
  const { date, is_laborable, is_leave } = calendarDay.attributes
  const dateFormatted = stringToDate(date, 'backend')
  const isToday = dateFormatted.isSame(moment(), 'day')

  if (isToday) return 'champagne100'

  const isDisabled = !is_laborable || is_leave

  if (isDisabled || !isTab) return 'grey100'

  return 'grey000White'
}

interface StyleDayRowBorderProps {
  hasFutureDate: boolean
  inconsistenciesTotal: number | undefined
  hasEditTimesheetAuthorization: boolean
  hasTimeTrackingInconsistencies: boolean
  hasUnresolvedShift: boolean | undefined
  hasPendingEditRequest: boolean
}

export const styleDayRowBorder = ({
  hasFutureDate,
  inconsistenciesTotal,
  hasEditTimesheetAuthorization,
  hasTimeTrackingInconsistencies,
  hasUnresolvedShift,
  hasPendingEditRequest,
}: StyleDayRowBorderProps): BorderStyle => {
  if (!hasFutureDate) {
    if (!!hasUnresolvedShift || (!!inconsistenciesTotal && hasTimeTrackingInconsistencies)) {
      return { width: 's4', style: 'solid', color: 'radicalRed1200' }
    }

    if (hasEditTimesheetAuthorization && !!hasPendingEditRequest) {
      return {
        color: 'champagne1000',
        width: 's4',
        style: 'solid',
      }
    }
  }

  return { width: 's1', style: 'solid', color: 'grey400' }
}

interface ShowShiftInputBorderStyles {
  hasEditTimesheetAuthorization: boolean
  isUnresolved: boolean
  pendingEditRequest: boolean
  isCreateShift: boolean
  hasShiftInconsistencies: boolean
}

export const showShiftInputBorderStyles = ({
  hasEditTimesheetAuthorization,
  isUnresolved,
  pendingEditRequest,
  isCreateShift,
  hasShiftInconsistencies,
}: ShowShiftInputBorderStyles) => {
  return (
    hasEditTimesheetAuthorization &&
    !isUnresolved &&
    !!pendingEditRequest &&
    !isCreateShift &&
    !hasShiftInconsistencies
  )
}

const createBorderStyle = (color: Colors): React.ComponentProps<typeof Box>['border'] => ({
  all: {
    width: 's2',
    style: 'solid',
    color,
  },
})

export const styleShiftInputBorder = (
  hasInputBorderStyle: boolean
): React.ComponentProps<typeof Box>['border'] => {
  if (hasInputBorderStyle) return createBorderStyle('champagne1000')
}

interface ShouldExpandDayRowParams {
  today: number
  isToday: boolean
  is_leave: boolean
  currentDay: number
  expandAll: boolean
  workedTime: number
  is_laborable: boolean
  estimatedWorkTime?: number
}

export const shouldExpandDayRow = ({
  today,
  isToday,
  is_leave,
  expandAll,
  currentDay,
  workedTime,
  is_laborable,
  estimatedWorkTime,
}: ShouldExpandDayRowParams): boolean => {
  return (
    !expandAll &&
    (isToday ||
      (currentDay < today &&
        !!estimatedWorkTime &&
        workedTime < estimatedWorkTime &&
        is_laborable &&
        !is_leave))
  )
}

interface IsGrayedOutParams {
  worked: number
  is_laborable: boolean
  is_leave: boolean
}

export const shouldGreyOut = ({ worked, is_laborable, is_leave }: IsGrayedOutParams): boolean => {
  return worked === 0 && (!is_laborable || is_leave)
}
